import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { UserAuthService } from '../UserAuth.service';
import { GoogleIdentityService } from '../auth/google-identity.service';
import { ExternalIdSignInService } from '../auth/external-id-sign-in.service';
import {
  capturePendingLink,
  clearPendingLink,
  landingPathFor,
  loadPendingLink,
  markSignInStarted,
  PendingLink,
  takeSignInStarted,
} from '../auth/pending-link';
import {
  IdentityModel,
  LinkInfo,
  LinkKind,
  LinksApiError,
  LinksApiService,
} from '../services/links-api.service';

/** What the page is showing. */
export type LandingView =
  | 'checking'      // links/info in flight
  | 'invalid'       // bad or partial link
  | 'not-pending'   // accepted / revoked / expired
  | 'unavailable'   // the link's sign-in model cannot be offered here (yet)
  | 'sign-in'       // one sign-in button, by model
  | 'confirm'       // someone is ALREADY signed in: accept as them, or use a different account
  | 'working'       // accept / redeem in flight
  | 'done'          // accepted / awaiting confirmation
  | 'returning'     // an External invite this person ALREADY accepted: offer sign-in to the app
  | 'error';        // a refusal with a server message

/** Refusals that mean "you signed in as someone else": offer a different account. */
export const WRONG_ACCOUNT_CODES = ['wrong-directory', 'wrong-account', 'wrong-provider'];

/**
 * /invite?c&i&s, public, no session needed to load. (/bind is retired: a
 * customer's directory is bound from its domains at Add Customer.)
 *
 * Flow: read the link (and strip it from the URL) → links/info → ONE sign-in
 * button for the link's model → after sign-in, accept the invite or redeem the
 * binding with the bearer. Server messages are shown as-is.
 *
 * External ID (CIAM): ExternalIdSignInService, a popup, so it is only ever
 * started from the button click. signIn() takes NO loginHint: links/info returns
 * no email on purpose. The interceptor attaches the CIAM token to invites/accept.
 *
 * BUT the interceptor prefers a workforce (MSAL) account, then Google, then CIAM.
 * With either of those sessions in this browser, invites/accept would go out
 * under THAT identity and be refused as the wrong provider, so the page asks for
 * a sign-out first instead of starting a sign-in whose token would never be sent.
 */
@Component({
  selector: 'app-link-landing',
  templateUrl: './link-landing.component.html',
  styleUrls: ['./link-landing.component.scss'],
})
export class LinkLandingComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Invites only, since binding links were retired. */
  readonly kind: LinkKind = 'invite';
  view: LandingView = 'checking';
  model: IdentityModel | null = null;
  /** Translation key for the fixed-text states. */
  messageKey = 'LinkChecking';
  /** A server message, shown verbatim when present. */
  serverMessage = '';
  wrongAccount = false;
  role = '';
  /** "Full Name (username)", or the username, of the session that is signed in. Never an id. */
  signedInAs = '';

  @ViewChild('googleHost') googleHost?: ElementRef<HTMLElement>;

  private link: PendingLink | null = null;
  /**
   * This load is the RETURN from a sign-in started on this page for this link. Only then may the
   * page accept without asking. Read once, at load.
   */
  private signInStartedHere = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private auth: UserAuthService,
    private googleIdentity: GoogleIdentityService,
    private links: LinksApiService,
    private externalId: ExternalIdSignInService
  ) {}

  ngOnInit(): void {
    this.link = capturePendingLink(this.kind);
    if (!this.link) {
      this.show('invalid', 'LinkInvalid');
      return;
    }
    this.signInStartedHere = takeSignInStarted(this.link.linkId);
    const { customerId, linkId, secret } = this.link;
    this.links.linkInfo(customerId, linkId, secret).subscribe({
      next: (info) => this.onInfo(info),
      error: (err: LinksApiError) => {
        if (err.status === 403 || err.code === 'bad-link') {
          clearPendingLink();
          this.show('invalid', 'LinkInvalid');
        } else {
          this.fail(err);
        }
      },
    });
  }

  ngAfterViewInit(): void {
    this.renderGoogleButtonIfNeeded();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** The Microsoft (workforce) button: return to this page after sign-in. */
  signInWithMicrosoft(): void {
    this.markStarted();
    this.auth.loginTo(landingPathFor(this.kind));
  }

  /** "Accept with this account", from the confirm view: the user chose the session that was there. */
  acceptAsSignedIn(): void {
    this.complete();
  }

  /**
   * The External ID button: a full-page redirect to the sign-in (no popup, Joel 2026-10-05). The
   * invite link is already in sessionStorage; after sign-in the app comes back to this page, which
   * finds the account (restore) and accepts. This only returns if the redirect could not start.
   */
  signInExternal(): void {
    this.show('working', 'LinkWorking');
    this.markStarted();
    this.externalId.signIn(landingPathFor(this.kind)).catch((err: unknown) => {
      console.warn('External ID sign-in could not start', (err as { errorCode?: string })?.errorCode ?? err);
      this.show('error', 'LinkSomethingWentWrong');
    });
  }

  /** "Already accepted? Sign in with email": a CIAM redirect straight back into the app ("/"). */
  signInReturning(): void {
    this.show('working', 'LinkWorking');
    this.externalId.signIn('/').catch((err: unknown) => {
      console.warn('External ID sign-in could not start', (err as { errorCode?: string })?.errorCode ?? err);
      this.show('error', 'LinkSomethingWentWrong');
    });
  }

  private offerReturningSignIn(): void {
    if (!this.externalId.enabled()) {
      this.show('not-pending', 'LinkAlreadyUsed');
      return;
    }
    if (this.hasWorkforceSession() || this.googleIdentity.isSignedIn()) {
      this.show('unavailable', 'LinkExternalSignOutFirst');
      return;
    }
    // Already signed in to External ID in this tab: straight in.
    this.externalId
      .restore()
      .then((account) => (account ? this.continueToApp() : this.show('returning', 'LinkAlreadyAcceptedSignIn')))
      .catch(() => this.show('returning', 'LinkAlreadyAcceptedSignIn'));
  }

  /** After a wrong-account refusal: pick a different account and try again. */
  useDifferentAccount(): void {
    this.wrongAccount = false;
    this.signedInAs = '';
    if (this.model === 'external') {
      this.externalId.signOut({ atAuthority: false }).finally(() => this.show('sign-in'));
    } else if (this.model === 'byot-google') {
      this.googleIdentity.signOut();
      this.show('sign-in');
      setTimeout(() => this.renderGoogleButtonIfNeeded());
    } else {
      // prompt=select_account (loginTo): the picker offers "Use another account", and the return
      // accepts with whoever is chosen.
      this.markStarted();
      this.auth.loginTo(landingPathFor(this.kind));
    }
  }

  continueToApp(): void {
    this.router.navigateByUrl('/');
  }

  // ---------------------------------------------------------------------------

  private onInfo(info: LinkInfo): void {
    // Only invites: a retired binding link is not a link.
    if (info.kind !== 'invite') {
      clearPendingLink();
      this.show('invalid', 'LinkInvalid');
      return;
    }
    this.model = info.model;
    // ALREADY ACCEPTED, External (2026-10-06): until /login has its own External sign-in, the
    // emailed link is how a returning External user gets back in. Offer sign-in INTO THE APP; the
    // invite itself is spent and is never sent again (the pending link is dropped here).
    if (info.status === 'accepted' && info.model === 'external') {
      clearPendingLink();
      this.offerReturningSignIn();
      return;
    }
    if (info.status !== 'pending') {
      clearPendingLink();
      this.show('not-pending', notPendingKey(info.status));
      return;
    }

    // NEVER ACCEPT WITH A SESSION THAT WAS ALREADY HERE (Joel, 2026-10-08). This used to accept at
    // once for any workforce session in the browser. A staff account open in the same Safari private
    // window was taken for the invitee and refused as "a different organisation", with no sign-in
    // step and no choice (DS trace: wrong-directory, tid = the staff tenant). Only the return from a
    // sign-in started HERE accepts outright; anyone else is shown and asked.
    if (info.model === 'byot-entra') {
      if (this.hasWorkforceSession()) {
        this.acceptOrConfirm(this.workforceName());
      } else {
        this.show('sign-in');
      }
      return;
    }

    if (info.model === 'byot-google') {
      if (!this.googleIdentity.isConfigured) {
        this.show('unavailable', 'LinkGoogleUnavailable');
        return;
      }
      if (this.googleIdentity.isSignedIn()) {
        // Already signed in to Google when the page loaded: not necessarily the invitee.
        this.confirmAccount(this.googleName());
        return;
      }
      this.show('sign-in');
      // A sign-in with the button on THIS page (Google renders it in place, no redirect): accept.
      this.googleIdentity.signedIn$
        .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          this.signedInAs = this.googleName();
          this.complete();
        });
      setTimeout(() => this.renderGoogleButtonIfNeeded());
      return;
    }

    // external (CIAM)
    if (!this.externalId.enabled()) {
      this.show('unavailable', 'LinkExternalUnavailable');
      return;
    }
    if (this.hasWorkforceSession() || this.googleIdentity.isSignedIn()) {
      this.show('unavailable', 'LinkExternalSignOutFirst');
      return;
    }
    // Back from the sign-in redirect, or a reload inside this tab: carry on as soon as the External
    // ID account is there. On a redirect RETURN this page mounts before AppComponent has finished
    // consuming the response (the router's first navigation is early), so restore() can still say
    // "nobody" here; the account then arrives on account$ and the invite is accepted from that.
    this.externalId.account$
      .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        // ONLY while this invite is still PENDING in storage (lead, 2026-10-05): a CIAM account
        // restored on some later visit must never accept a link that was already used or dropped.
        if (!this.linkStillPending()) return;
        if (this.view === 'sign-in' || this.view === 'checking') this.acceptOrConfirm(this.externalName());
      });
    this.externalId
      .restore()
      .then((account) =>
        account ? this.acceptOrConfirm(this.externalName()) : this.view === 'checking' && this.show('sign-in')
      )
      .catch(() => this.view === 'checking' && this.show('sign-in'));
  }

  /**
   * A WORKFORCE (MSAL) session. isAuthenticated() also counts an External ID
   * session now, and that one must neither auto-accept an Entra invite nor
   * block its own External ID invite.
   */
  private hasWorkforceSession(): boolean {
    return this.auth.isAuthenticated() && !this.auth.isExternalIdAuthenticated();
  }

  /** The return from a sign-in started here accepts; a session that was already here is asked about. */
  private acceptOrConfirm(who: string): void {
    this.signedInAs = who;
    if (this.signInStartedHere) {
      this.complete();
    } else {
      this.confirmAccount(who);
    }
  }

  private confirmAccount(who: string): void {
    this.signedInAs = who;
    this.show('confirm');
  }

  private markStarted(): void {
    if (this.link) markSignInStarted(this.link.linkId);
  }

  private workforceName(): string {
    return displayAccount(this.auth.getActiveAccountInfo());
  }

  private externalName(): string {
    return displayAccount(this.externalId.account());
  }

  private googleName(): string {
    let email: string | null = null;
    this.googleIdentity.email$.pipe(take(1)).subscribe((e) => (email = e));
    return email ?? '';
  }

  /** Accept the invite, with the bearer of whoever signed in. */
  /** The link this page holds is the one still pending in sessionStorage (not consumed or replaced). */
  private linkStillPending(): boolean {
    const stored = loadPendingLink();
    return !!stored && !!this.link && stored.linkId === this.link.linkId && stored.customerId === this.link.customerId;
  }

  /** Set once accept/redeem has been sent: two routes (restore and account$) can both arrive here. */
  private completing = false;

  private complete(): void {
    if (!this.link || this.completing) return;
    this.completing = true;
    const { customerId, linkId, secret } = this.link;
    this.show('working', 'LinkWorking');
    this.links.acceptInvite(customerId, linkId, secret).subscribe({
      next: (r) => {
        clearPendingLink();
        this.role = r?.role ?? '';
        this.show('done', 'InviteDone');
        // "You're in" long enough to read, then the normal app landing.
        setTimeout(() => this.continueToApp(), 1500);
      },
      error: (err: LinksApiError) => this.fail(err),
    });
  }

  private fail(err: LinksApiError): void {
    this.completing = false;
    this.wrongAccount = WRONG_ACCOUNT_CODES.includes(err.code ?? '');
    // 409 already used / 410 expired are final: the link will never work again.
    if (err.status === 409 || err.status === 410) clearPendingLink();
    this.serverMessage = err.serverMessage ?? '';
    this.show('error', err.serverMessage ? '' : 'LinkSomethingWentWrong');
  }

  private show(view: LandingView, messageKey = ''): void {
    this.view = view;
    this.messageKey = messageKey;
    if (view !== 'error') this.serverMessage = '';
  }

  private renderGoogleButtonIfNeeded(): void {
    if (this.view !== 'sign-in' || this.model !== 'byot-google' || !this.googleHost) return;
    this.googleIdentity.renderButton(this.googleHost.nativeElement).catch(() => {
      this.show('unavailable', 'LinkGoogleUnavailable');
    });
  }
}

/**
 * "Full Name (username)", or just the username. Joel's rule: never a raw id (no oid, no tid), so
 * nothing else from the account is ever shown.
 */
export function displayAccount(account: { name?: string; username?: string } | null | undefined): string {
  const name = account?.name?.trim() ?? '';
  const user = account?.username?.trim() ?? '';
  if (name && user && name !== user) return `${name} (${user})`;
  return user || name;
}

export function notPendingKey(status: string): string {
  switch (status) {
    case 'accepted':
      return 'LinkAlreadyUsed';
    case 'expired':
      return 'LinkExpired';
    case 'revoked':
      return 'LinkRevoked';
    default:
      return 'LinkInvalid';
  }
}
